import { leadInPlainText } from "@/lib/lead-in-html"
import { SHOWCASE_PACEMAKER_PASSAGE } from "@/lib/question-preview"

import { classificationChecks } from "./classification-ask"
import { sortQuestionQualityChecks } from "./confidence"
import {
  namedSetLabel,
  topicCompleteDistractorSets,
  unusedTopicDistractors,
} from "./topic-options"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySettingsTarget,
  QuestionQualitySnapshot,
} from "./types"

function stemRewrites(stem: string): { primary: string; extra?: string } {
  if (stem.toLowerCase().includes("citric")) {
    return {
      primary: "Where does the citric acid cycle take place?",
      extra: "In which cellular compartment does the citric acid cycle take place?",
    }
  }
  if (stem.toLowerCase().includes("beta oxidation of fatty acids occurs")) {
    return {
      primary: "Where does beta oxidation of fatty acids occur?",
      extra: "In which cellular compartment does beta oxidation of fatty acids occur?",
    }
  }
  if (stem.endsWith(":")) {
    return { primary: `What ${stem.slice(0, -1).trim().toLowerCase()}?` }
  }
  return { primary: `${stem.replace(/\.*$/, "")}?` }
}

const STEM_PROMISES_REFERENCE =
  /\b(?:read the following(?:\s+(?:passage|excerpt|text|paragraph|article))?|the following (?:passage|excerpt|text|paragraph|article)|attached (?:pdf|document|file|image|figure|passage)|the attached|see the (?:passage|attachment|figure|table)|provided (?:passage|excerpt|diagram|figure|attachment)|according to the (?:passage|excerpt|article))\b/i

function stemReferenceMark(stem: string): string {
  return stem.match(STEM_PROMISES_REFERENCE)?.[0] ?? "the following passage"
}

function itemNeedsReference(stem: string): boolean {
  return STEM_PROMISES_REFERENCE.test(stem)
}

const MISSING_REFERENCE_CHECK_ID = "compliance-missing-reference"

function suggestedReferenceHtml(stem: string): string {
  const lower = stem.toLowerCase()
  if (
    /heartbeat|pacemaker|sinoatrial|sa node|cardiac rhythm|normal heartbeat/.test(
      lower,
    )
  ) {
    return SHOWCASE_PACEMAKER_PASSAGE
  }
  const question = stem
    .replace(STEM_PROMISES_REFERENCE, "")
    .replace(/^[.\s]+/, "")
    .replace(/\?.*$/, "")
    .trim()
  if (!question) {
    return "<p>Supporting passage for this item.</p>"
  }
  return `<p>Supporting passage for: ${question}.</p>`
}

function missingReferenceAlternates(stem: string): QuestionQualityAlternate[] {
  const referencesHtml = suggestedReferenceHtml(stem)
  const plain = referencesHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
  const label =
    referencesHtml === SHOWCASE_PACEMAKER_PASSAGE
      ? "SA node pacemaker passage"
      : plain.length > 72
        ? `${plain.slice(0, 69)}…`
        : plain
  return [
    {
      id: "missing-passage-suggested",
      text: label,
      actionKind: "apply-text",
      actionLabel: "Use this passage",
      patch: { referencesHtml },
      parameterDeltas: [
        { parameter: "compliance", effect: "clears" },
        { parameter: "cognitive", effect: "locked" },
      ],
      clearsCheckIds: [MISSING_REFERENCE_CHECK_ID],
    },
  ]
}

function unstatedOptionRewrites(optionText: string): string[] {
  const lower = optionText.toLowerCase()
  if (lower.includes("third stated")) {
    return ["Cytoplasm", "Nucleus"]
  }
  if (lower.includes("first stated")) {
    return ["Endoplasmic reticulum", "Golgi apparatus"]
  }
  if (lower.includes("second stated")) {
    return ["Plasma membrane", "Lysosome"]
  }
  return ["A named structure in this topic", "A process described in the stem"]
}

function unstatedOptionAlternates(
  option: { id: string; text: string },
): QuestionQualityAlternate[] {
  const checkId = `distractor-unstated-${option.id}`
  return unstatedOptionRewrites(option.text).map((text, index) => ({
    id: `unstated-${option.id}-${index}`,
    text,
    actionKind: "apply-text" as const,
    actionLabel: "Use this distractor",
    patch: { optionText: text },
    parameterDeltas: [
      { parameter: "distractor" as const, effect: "clears" as const },
      { parameter: "cognitive" as const, effect: "locked" as const },
    ],
    clearsCheckIds: [checkId],
  }))
}

function applyTextAlternates(
  idPrefix: string,
  texts: string[],
  actionLabel: string,
  patchFor: (text: string) => QuestionQualityAlternate["patch"],
  clearsCheckIds: string[],
  parameter: QuestionQualityAlternate["parameterDeltas"][number]["parameter"],
): QuestionQualityAlternate[] {
  return texts.map((text, index) => ({
    id: `${idPrefix}-${index}`,
    text,
    actionKind: "apply-text" as const,
    actionLabel,
    patch: patchFor(text),
    parameterDeltas: [
      { parameter, effect: "clears" as const },
      { parameter: "cognitive" as const, effect: "locked" as const },
    ],
    clearsCheckIds,
  }))
}

function titleFromStem(stem: string, fallback: string): string {
  const words = stem
    .replace(/[?:].*$/, "")
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .slice(0, 6)
  const next = words.join(" ")
  return next.length >= 8 ? next : fallback
}

function stemAlternates(stem: string): QuestionQualityAlternate[] {
  const rewrites = stemRewrites(stem)
  const clearsCheckIds = [
    "compliance-incomplete-stem",
    "clarity-fragment-stem",
  ]
  const parameterDeltas = [
    { parameter: "clarity" as const, effect: "clears" as const },
    { parameter: "compliance" as const, effect: "clears" as const },
    { parameter: "cognitive" as const, effect: "locked" as const },
  ]
  const items: QuestionQualityAlternate[] = [
    {
      id: "stem-complete",
      text: rewrites.primary,
      actionKind: "apply-text",
      actionLabel: "Use this stem",
      patch: { leadIn: rewrites.primary },
      parameterDeltas,
      clearsCheckIds,
    },
  ]
  if (rewrites.extra) {
    items.push({
      id: "stem-compartment",
      text: rewrites.extra,
      actionKind: "apply-text",
      actionLabel: "Use this stem",
      patch: { leadIn: rewrites.extra },
      parameterDeltas,
      clearsCheckIds,
    })
  }
  return items
}

const NOTA = /none of the above/i
const AOTA = /all of the above/i
const STATED = /\b(first|second|third|fourth|last)\s+stated\b/i
const NEGATIVE = /\b(not|except|unless)\b/i

export const QUALITY_RATIONALE_EMPTY_MARK =
  "Cite the mechanism, guideline, or teaching point for reviewers."

export function isPositionalOptionText(text: string): boolean {
  const t = text.trim().toLowerCase()
  if (/^(all|none|both|either) of the above$/.test(t)) return true
  if (STATED.test(t)) return true
  return false
}

function plainLeadIn(html: string): string {
  return leadInPlainText(html)
}

export function runQuestionQualityChecks(
  snapshot: QuestionQualitySnapshot,
): QuestionQualityCheck[] {
  const checks: QuestionQualityCheck[] = []
  const stem = plainLeadIn(snapshot.leadIn)
  const options = snapshot.options
  const isChoice =
    snapshot.type === "mcq_single" ||
    snapshot.type === "mcq_multiple" ||
    snapshot.type === "true_false"

  if (stem && (stem.endsWith(":") || (!stem.includes("?") && stem.split(/\s+/).length < 12))) {
    const alternates = stemAlternates(stem)
    const primaryLeadIn = alternates[0]?.patch?.leadIn
    if (stem.endsWith(":")) {
      checks.push({
        id: "compliance-incomplete-stem",
        parameter: "compliance",
        severity: "caution",
        field: "leadIn",
        title: "Incomplete stem",
        mechanism: "Item-writing guidance asks for a complete question, not a phrase the student finishes.",
        markText: stem,
        currentText: stem,
        rubricConfidence: "high",
        actionKind: "apply-text",
        actionLabel: "Use this stem",
        patch: primaryLeadIn ? { leadIn: primaryLeadIn } : undefined,
        alternates,
      })
    }
    checks.push({
      id: "clarity-fragment-stem",
      parameter: "clarity",
      severity: "caution",
      field: "leadIn",
      title: "Stem is a fragment",
      mechanism: "The prompt is not a complete question, so the item asks for recall of a phrase rather than a decision.",
      markText: stem,
      currentText: stem,
      rubricConfidence: stem.endsWith(":") ? "high" : "medium",
      actionKind: "apply-text",
      actionLabel: "Use this stem",
      patch: primaryLeadIn ? { leadIn: primaryLeadIn } : undefined,
      alternates,
    })
  }

  if (itemNeedsReference(stem) && !snapshot.hasReferences) {
    const marked = stemReferenceMark(stem)
    const alternates = missingReferenceAlternates(stem)
    const primary = alternates[0]
    checks.push({
      id: MISSING_REFERENCE_CHECK_ID,
      parameter: "compliance",
      severity: "review",
      field: "references",
      title: "Supporting material is missing",
      mechanism:
        "The stem asks students to use a passage, file, or figure that is not in References. Use the suggested passage to complete the item.",
      markText: marked,
      currentText: marked,
      rubricConfidence: "high",
      actionKind: primary?.actionKind ?? "none",
      actionLabel: primary?.actionLabel ?? "Add it in References",
      patch: primary?.patch,
      alternates,
    })
  }

  if (NEGATIVE.test(stem) && !/\b(NOT|EXCEPT)\b/.test(stem)) {
    const marked = stem.match(NEGATIVE)?.[0] ?? "not"
    const markedUp = marked.toLowerCase() === "except" ? "EXCEPT" : "NOT"
    const nextStem = stem.replace(new RegExp(`\\b${marked}\\b`, "i"), markedUp)
    checks.push({
      id: "clarity-unmarked-negative",
      parameter: "clarity",
      severity: "review",
      field: "leadIn",
      title: "Unmarked negative",
      mechanism: "NOT or EXCEPT is easy to miss when it is not marked, so some students fail the item for reading, not knowledge.",
      markText: marked,
      currentText: stem,
      rubricConfidence: "high",
      actionKind: "apply-text",
      actionLabel: "Mark the negative",
      patch: { leadIn: nextStem },
      alternates: [
        {
          id: "mark-negative",
          text: nextStem,
          actionKind: "apply-text",
          actionLabel: "Mark the negative",
          patch: { leadIn: nextStem },
          parameterDeltas: [
            { parameter: "clarity", effect: "clears" },
            { parameter: "cognitive", effect: "locked" },
          ],
          clearsCheckIds: ["clarity-unmarked-negative"],
        },
      ],
    })
  }

  const title = snapshot.title.trim()
  if (stem && (!title || title.length < 8)) {
    const longer = titleFromStem(stem, title ? `${title} item` : "Untitled item")
    const titled = longer === title ? `${title} search title` : longer
    const alternates = applyTextAlternates(
      "short-title",
      [titled, `${title} in this course`],
      "Use this title",
      (text) => ({ title: text }),
      ["clarity-short-title"],
      "clarity",
    )
    checks.push({
      id: "clarity-short-title",
      parameter: "clarity",
      severity: "caution",
      field: "title",
      title: "Title is too short to find later",
      mechanism: "Bank search needs a name colleagues can recognise.",
      markText: title || undefined,
      currentText: title || "None",
      rubricConfidence: "medium",
      actionKind: "apply-text",
      actionLabel: "Use this title",
      patch: alternates[0]?.patch,
      alternates,
    })
  }

  if (title && stem) {
    const words = (value: string) =>
      value
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((word) => word.length > 3)
    const titleWords = new Set(words(title))
    const stemWords = words(stem)
    const overlap = stemWords.filter((word) => titleWords.has(word)).length
    if (titleWords.size >= 2 && overlap === 0) {
      const aligned = titleFromStem(stem, "Item from this stem")
      const alternates = applyTextAlternates(
        "title-match",
        [aligned, `${aligned} item`],
        "Use this title",
        (text) => ({ title: text }),
        ["clarity-title-mismatch"],
        "clarity",
      )
      checks.push({
        id: "clarity-title-mismatch",
        parameter: "clarity",
        severity: "caution",
        field: "title",
        title: "Title does not match the stem",
        mechanism: "Bank search will miss this item if the title names a different topic.",
        markText: title,
        currentText: title,
        rubricConfidence: "medium",
        actionKind: "apply-text",
        actionLabel: "Use this title",
        patch: alternates[0]?.patch,
        alternates,
      })
    }
  }

  const stemWords = stem.split(/\s+/).filter(Boolean)
  const longWords = stemWords.filter((word) => word.replace(/[^a-zA-Z]/g, "").length >= 12)
  if (stemWords.length >= 18 && longWords.length >= 3) {
    const simple = stem.toLowerCase().includes("ketone")
      ? "What do ketone bodies do to blood pH during fasting?"
      : `What is the effect in this case?`
    const simpler = stem.toLowerCase().includes("ketone")
      ? "How do ketone bodies change blood pH?"
      : stem.replace(/\b\w{12,}\b/g, "change").replace(/\s+/g, " ").trim()
    const alternates = applyTextAlternates(
      "reading",
      [simple, simpler],
      "Use this stem",
      (text) => ({ leadIn: text }),
      ["clarity-reading-level"],
      "clarity",
    )
    checks.push({
      id: "clarity-reading-level",
      parameter: "clarity",
      severity: "caution",
      field: "leadIn",
      title: "Stem may be harder to read than the course needs",
      mechanism: "A simpler stem keeps the item about the construct, not reading load.",
      markText: stem,
      currentText: stem,
      rubricConfidence: "low",
      actionKind: "apply-text",
      actionLabel: "Use this stem",
      patch: alternates[0]?.patch,
      alternates,
    })
  }

  if (isChoice) {
    const keyedNota = options.find((o) => o.isCorrect && NOTA.test(o.text))
    if (keyedNota) {
      const citric = stem.toLowerCase().includes("citric")
      const keys = citric
        ? ["Mitochondrial matrix", "Matrix of the mitochondrion"]
        : ["A named correct fact", "The keyed process for this stem"]
      const alternates = applyTextAlternates(
        "keyed-nota",
        keys,
        "Use this distractor",
        (text) => ({ optionText: text }),
        ["distractor-keyed-nota"],
        "distractor",
      )
      checks.push({
        id: "distractor-keyed-nota",
        parameter: "distractor",
        severity: "review",
        field: "option",
        optionId: keyedNota.id,
        title: "None of the above is the key",
        mechanism: "The item measures elimination rather than knowledge of the fact.",
        markText: keyedNota.text.trim(),
        currentText: keyedNota.text.trim(),
        rubricConfidence: "high",
        actionKind: "apply-text",
        actionLabel: "Use this distractor",
        patch: alternates[0]?.patch,
        alternates,
      })
    }

    const hasNota = options.some((o) => NOTA.test(o.text))
    const hasAota = options.some((o) => AOTA.test(o.text))
    const unusedNames = unusedTopicDistractors(
      stem,
      options.map((option) => option.text),
    )
    let unusedAt = 0
    const takeUnused = (count: number): string[] => {
      const slice = unusedNames.slice(unusedAt, unusedAt + count)
      unusedAt += slice.length
      return slice
    }
    const statedOthers = options.filter(
      (option) =>
        STATED.test(option.text) &&
        !AOTA.test(option.text) &&
        !NOTA.test(option.text),
    )
    const aotaRewrittenIds = new Set<string>()
    if (hasNota && hasAota) {
      const aota = options.find((o) => AOTA.test(o.text))
      const nota = options.find((o) => NOTA.test(o.text))
      const citric = stem.toLowerCase().includes("citric")
      const notaIsKey = Boolean(nota?.isCorrect)
      const pairs = citric
        ? notaIsKey
          ? [
              {
                label: "Intermembrane space, and mitochondrial matrix",
                optionTexts: [
                  {
                    id: aota?.id ?? "",
                    text: "Intermembrane space",
                    from: aota?.text.trim(),
                  },
                  {
                    id: nota?.id ?? "",
                    text: "Mitochondrial matrix",
                    from: nota?.text.trim(),
                  },
                ],
              },
              {
                label: "Cristae, and matrix of the mitochondrion",
                optionTexts: [
                  {
                    id: aota?.id ?? "",
                    text: "Cristae",
                    from: aota?.text.trim(),
                  },
                  {
                    id: nota?.id ?? "",
                    text: "Matrix of the mitochondrion",
                    from: nota?.text.trim(),
                  },
                ],
              },
            ]
          : [
              {
                label: "Intermembrane space, and cytosol",
                optionTexts: [
                  {
                    id: aota?.id ?? "",
                    text: "Intermembrane space",
                    from: aota?.text.trim(),
                  },
                  {
                    id: nota?.id ?? "",
                    text: "Cytosol",
                    from: nota?.text.trim(),
                  },
                ],
              },
              {
                label: "Cristae, and peroxisome",
                optionTexts: [
                  {
                    id: aota?.id ?? "",
                    text: "Cristae",
                    from: aota?.text.trim(),
                  },
                  {
                    id: nota?.id ?? "",
                    text: "Peroxisome",
                    from: nota?.text.trim(),
                  },
                ],
              },
            ]
        : (() => {
            const needed = 2 + statedOthers.length
            const toRows = (names: string[]) =>
              [
                {
                  id: aota?.id ?? "",
                  text: names[0] ?? "",
                  from: aota?.text.trim(),
                },
                {
                  id: nota?.id ?? "",
                  text: names[1] ?? "",
                  from: nota?.text.trim(),
                },
                ...statedOthers.map((option, index) => ({
                  id: option.id,
                  text: names[2 + index] ?? "",
                  from: option.text.trim(),
                })),
              ].filter((row) => row.id && row.text)
            const namedPairs = topicCompleteDistractorSets(
              stem,
              options.map((option) => option.text),
              needed,
            )
            if (namedPairs.length === 0) {
              return [
                {
                  label: "Two named facts in place of All and None",
                  optionTexts: toRows([
                    "A named fact from the topic",
                    "A second named fact from the topic",
                  ]),
                },
              ]
            }
            const key = options.find(
              (option) =>
                option.isCorrect &&
                !AOTA.test(option.text) &&
                !NOTA.test(option.text),
            )
            return namedPairs.map((names) => {
              const rows = toRows(names)
              const optionTexts = key
                ? [
                    {
                      id: key.id,
                      text: key.text.trim(),
                      from: key.text.trim(),
                    },
                    ...rows,
                  ]
                : rows
              return {
                label: namedSetLabel(names),
                optionTexts,
              }
            })
          })()
      const alternates = pairs.map((pair, index) => {
        const optionTexts = pair.optionTexts.filter((row) => row.id)
        for (const row of optionTexts) aotaRewrittenIds.add(row.id)
        const unstatedIds = optionTexts
          .map((row) => row.id)
          .filter((id) => statedOthers.some((option) => option.id === id))
          .map((id) => `distractor-unstated-${id}`)
        return {
          id: `aota-nota-${index}`,
          text: pair.label,
          actionKind: "apply-text" as const,
          actionLabel:
            optionTexts.length > 2 ? "Use these options" : "Replace both options",
          patch: { optionTexts },
          parameterDeltas: [
            { parameter: "distractor" as const, effect: "clears" as const },
            { parameter: "cognitive" as const, effect: "locked" as const },
          ],
          clearsCheckIds: [
            "distractor-aota-nota",
            "distractor-keyed-nota",
            "distractor-too-few",
            ...unstatedIds,
          ],
        }
      })
      checks.push({
        id: "distractor-aota-nota",
        parameter: "distractor",
        severity: "review",
        field: "options",
        title: "All of the above and None of the above both appear",
        mechanism: "The two options cannot both be plausible on the same item.",
        markText: aota?.text.trim() ?? "All of the above",
        currentText: aota?.text.trim(),
        rubricConfidence: "high",
        actionKind: "apply-text",
        actionLabel: alternates[0]?.actionLabel ?? "Replace both options",
        patch: alternates[0]?.patch,
        alternates,
      })
    }

    for (const option of options) {
      if (aotaRewrittenIds.has(option.id)) continue
      if (STATED.test(option.text) && !/mechanism|step|reason/i.test(stem)) {
        const fromTopic = stem.toLowerCase().includes("citric")
          ? []
          : takeUnused(2)
        const alternates =
          fromTopic.length >= 2
            ? applyTextAlternates(
                `unstated-${option.id}`,
                fromTopic,
                "Use this distractor",
                (text) => ({ optionText: text }),
                [`distractor-unstated-${option.id}`],
                "distractor",
              )
            : unstatedOptionAlternates(option)
        checks.push({
          id: `distractor-unstated-${option.id}`,
          parameter: "distractor",
          severity: "review",
          field: "option",
          optionId: option.id,
          title: "Option refers to something the stem never states",
          mechanism: "Students cannot judge a stated mechanism that is not in the prompt.",
          markText: option.text.trim(),
          currentText: option.text.trim(),
          rubricConfidence: "high",
          actionKind: "apply-text",
          actionLabel: "Use this distractor",
          patch: alternates[0]?.patch,
          alternates,
        })
      }
    }

    const key = options.find((o) => o.isCorrect)
    if (key) {
      const keyLen = key.text.trim().length
      const others = options.filter((o) => o.id !== key.id && o.text.trim())
      if (
        others.length > 0 &&
        others.every((o) => keyLen >= o.text.trim().length * 1.6 && keyLen - o.text.trim().length >= 12)
      ) {
        const shortKey = key.text.toLowerCase().includes("aspartate")
          ? "Aspartate"
          : key.text.trim().split(/\s+/).slice(0, 3).join(" ")
        const shorter = key.text.toLowerCase().includes("aspartate")
          ? "Asp"
          : key.text.trim().slice(0, Math.max(8, Math.round(key.text.trim().length / 3)))
        const alternates = applyTextAlternates(
          "longest-key",
          [shortKey, shorter],
          "Use this distractor",
          (text) => ({ optionText: text }),
          ["cueing-longest-key"],
          "cueing",
        )
        checks.push({
          id: "cueing-longest-key",
          parameter: "cueing",
          severity: "caution",
          field: "option",
          optionId: key.id,
          title: "The longest option is the key",
          mechanism: "Length can cue the answer when distractors are much shorter.",
          markText: key.text.trim(),
          currentText: key.text.trim(),
          rubricConfidence: "medium",
          actionKind: "apply-text",
          actionLabel: "Use this distractor",
          patch: alternates[0]?.patch,
          alternates,
        })
      }
    }

    const articleCue = stem.match(/\b(a|an)\s*$/i)
    if (articleCue && key) {
      const wantAn = articleCue[1].toLowerCase() === "an"
      const fits = (text: string) => /^[aeiou]/i.test(text.trim())
      const keyFits = fits(key.text) === wantAn
      const distractorFits = options.filter(
        (option) => !option.isCorrect && option.text.trim() && fits(option.text) === wantAn,
      )
      if (keyFits && distractorFits.length === 0) {
        const topic = stem.replace(/\s+(a|an)\s*$/i, "").trim()
        const closedStem = `${topic}?`
        const erythrocytes = stem.toLowerCase().includes("erythrocytes")
        const openStem = erythrocytes
          ? "What is the principal intracellular buffer in erythrocytes?"
          : `What is ${topic.toLowerCase()}?`
        const whichStem = erythrocytes
          ? "Which substance is the principal intracellular buffer in erythrocytes?"
          : `Which substance is ${topic.toLowerCase()}?`
        const nameStem = erythrocytes
          ? "Name the principal intracellular buffer in erythrocytes."
          : `Name ${topic.toLowerCase()}.`
        const stemAlternatesForGrammar = applyTextAlternates(
          "grammar-stem",
          [closedStem, openStem, whichStem, nameStem],
          "Use this stem",
          (text) => ({ leadIn: text }),
          ["cueing-grammar-key"],
          "cueing",
        )
        checks.push({
          id: "cueing-grammar-key",
          parameter: "cueing",
          severity: "review",
          field: "leadIn",
          title: "Only the key fits the stem grammar",
          mechanism: "If the stem ends with a or an, every option should be grammatically possible.",
          markText: articleCue[0] ?? articleCue[1],
          currentText: stem,
          rubricConfidence: "high",
          actionKind: "apply-text",
          actionLabel: "Use this stem",
          patch: stemAlternatesForGrammar[0]?.patch,
          alternates: stemAlternatesForGrammar,
        })
        const vowelDistractor = options.find(
          (option) => !option.isCorrect && option.text.trim() && !/^[aeiou]/i.test(option.text.trim()),
        )
        if (vowelDistractor) {
          const next = wantAn ? "acetate" : "chloride"
          const extra = wantAn ? "ammonium" : "chloride ion"
          const third = wantAn ? "aspartate" : "sulfate"
          const fourth = wantAn ? "oxalate" : "nitrate"
          const optionAlternates = applyTextAlternates(
            `grammar-option-${vowelDistractor.id}`,
            [next, extra, third, fourth],
            "Use this distractor",
            (text) => ({
              optionTexts: [
                {
                  id: vowelDistractor.id,
                  text,
                  from: vowelDistractor.text.trim(),
                },
              ],
            }),
            [`cueing-grammar-option-${vowelDistractor.id}`],
            "cueing",
          )
          checks.push({
            id: `cueing-grammar-option-${vowelDistractor.id}`,
            parameter: "cueing",
            severity: "review",
            field: "option",
            optionId: vowelDistractor.id,
            title: "This option does not fit the stem grammar",
            mechanism: "If the stem ends with a or an, every option should be grammatically possible.",
            markText: vowelDistractor.text.trim(),
            currentText: vowelDistractor.text.trim(),
            rubricConfidence: "high",
            actionKind: "apply-text",
            actionLabel: "Use this distractor",
            patch: optionAlternates[0]?.patch,
            alternates: optionAlternates,
          })
        }
      }
    }

    const functional = options.filter(
      (o) => !o.isCorrect && o.text.trim() && !NOTA.test(o.text) && !AOTA.test(o.text),
    )
    if (
      snapshot.type === "mcq_single" &&
      functional.length < 2 &&
      !(hasNota && hasAota)
    ) {
      const nota = options.find((o) => NOTA.test(o.text))
      const named = unusedTopicDistractors(
        stem,
        options.map((option) => option.text),
      )
      const extras = stem.toLowerCase().includes("vmax")
        ? ["Km", "1/Vmax"]
        : named.length >= 2
          ? named.slice(0, 2)
          : ["A named incorrect fact", "A second named incorrect fact"]
      const replaceNota = nota
        ? extras.slice(0, 1).map((text) => ({
            id: nota.id,
            text,
            from: nota.text.trim(),
          }))
        : []
      const addOptions = extras.slice(nota ? 1 : 0)
      const alternates: QuestionQualityAlternate[] = [
        {
          id: "too-few-fill",
          text: extras.join(", "),
          actionKind: "apply-text",
          actionLabel: "Use these options",
          patch: {
            optionTexts: replaceNota,
            addOptions,
          },
          parameterDeltas: [
            { parameter: "distractor", effect: "clears" },
            { parameter: "cognitive", effect: "locked" },
          ],
          clearsCheckIds: ["distractor-too-few", "distractor-keyed-nota"],
        },
      ]
      checks.push({
        id: "distractor-too-few",
        parameter: "distractor",
        severity: "review",
        field: "options",
        title: "Fewer than two functional distractors",
        mechanism: "The item cannot separate strong students from weak ones if only one wrong choice is real.",
        markText: nota?.text.trim() ?? options[0]?.text.trim(),
        currentText: nota?.text.trim() ?? options[0]?.text.trim(),
        rubricConfidence: "high",
        actionKind: "apply-text",
        actionLabel: "Use these options",
        patch: alternates[0]?.patch,
        alternates,
      })
    }
  }

  const hasKeyRationale =
    snapshot.rationale.trim().length > 0 ||
    options.some((o) => o.isCorrect && o.rationale.trim())
  if (isChoice && !hasKeyRationale) {
    const rationaleText = stem.toLowerCase().includes("citric")
      ? "The citric acid cycle occurs in the mitochondrial matrix."
      : "State why the keyed option is correct and why each distractor is not."
    checks.push({
      id: "compliance-no-rationale",
      parameter: "compliance",
      severity: "caution",
      field: "rationale",
      title: "No rationale for the key",
      mechanism: "Reviewers and students cannot see why the key is right.",
      markText: QUALITY_RATIONALE_EMPTY_MARK,
      currentText: QUALITY_RATIONALE_EMPTY_MARK,
      rubricConfidence: "high",
      actionKind: "set-rationale",
      actionLabel: "Add a rationale",
      patch: { rationale: rationaleText },
      alternates: [
        {
          id: "add-rationale",
          text: rationaleText,
          actionKind: "set-rationale",
          actionLabel: "Add a rationale",
          patch: { rationale: rationaleText },
          parameterDeltas: [
            { parameter: "compliance", effect: "clears" },
            { parameter: "cognitive", effect: "locked" },
          ],
          clearsCheckIds: ["compliance-no-rationale"],
        },
      ],
    })
  }

  if (
    snapshot.type === "mcq_single" &&
    /\bselect all\b/i.test(stem)
  ) {
    checks.push({
      id: "format-select-all",
      parameter: "format",
      severity: "caution",
      field: "settings",
      settingsTarget: "format",
      title: "Format may not fit the stem",
      mechanism: "Select all belongs on multi select. Changing format creates a new item and changes sitting time.",
      currentText: "Single choice",
      rubricConfidence: "high",
      actionKind: "change-format",
      actionLabel: "Change format",
      patch: { type: "mcq_multiple" },
      alternates: [
        {
          id: "to-multi-select",
          text: "Change to Multi select. Select all belongs on that format.",
          actionKind: "change-format",
          actionLabel: "Change format",
          patch: { type: "mcq_multiple" },
          parameterDeltas: [
            { parameter: "format", effect: "clears" },
            { parameter: "cognitive", effect: "locked" },
          ],
          clearsCheckIds: ["format-select-all"],
        },
      ],
    })
  }

  if (
    snapshot.type === "true_false" &&
    /\bwhich of the following\b/i.test(stem)
  ) {
    checks.push({
      id: "format-misfit",
      parameter: "format",
      severity: "caution",
      field: "settings",
      settingsTarget: "format",
      title: "Format may not fit the stem",
      mechanism: "Which of the following belongs on a choice item. Changing format creates a new item.",
      currentText: "True or false",
      rubricConfidence: "high",
      actionKind: "change-format",
      actionLabel: "Change format",
      patch: { type: "mcq_single" },
      alternates: [
        {
          id: "to-single-choice",
          text: "Change to Single choice. Which of the following belongs on a choice item.",
          actionKind: "change-format",
          actionLabel: "Change format",
          patch: { type: "mcq_single" },
          parameterDeltas: [
            { parameter: "format", effect: "clears" },
            { parameter: "cognitive", effect: "locked" },
          ],
          clearsCheckIds: ["format-misfit"],
        },
      ],
    })
  }

  checks.push(
    ...classificationChecks(
      snapshot,
      { bloom: true, difficulty: true },
      { when: "mismatch" },
    ),
  )

  return checks
}

export function visibleQuestionQualityChecks(
  checks: QuestionQualityCheck[],
  dismissedIds: string[],
): QuestionQualityCheck[] {
  const dismissed = new Set(dismissedIds)
  return sortQuestionQualityChecks(checks.filter((check) => !dismissed.has(check.id)))
}

export function primaryQuestionQualityCheck(
  checks: QuestionQualityCheck[],
  field: QuestionQualityCheck["field"],
  optionId?: string,
): QuestionQualityCheck | undefined {
  const matches = checks.filter((check) => {
    if (check.field !== field) return false
    if (field === "option") return check.optionId === optionId
    return true
  })
  return (
    matches.find((check) => check.severity === "review") ?? matches[0]
  )
}

export function hasOpenQuestionQualityReview(
  checks: QuestionQualityCheck[],
): boolean {
  return checks.some((check) => check.severity === "review")
}

export function settingsTargetForCheck(
  check: QuestionQualityCheck,
): QuestionQualitySettingsTarget | null {
  if (check.settingsTarget) return check.settingsTarget
  if (check.parameter === "format") return "format"
  return null
}

export function qualityCheckForSettings(
  checks: QuestionQualityCheck[],
  target: QuestionQualitySettingsTarget,
): QuestionQualityCheck | undefined {
  const matches = checks.filter(
    (check) => settingsTargetForCheck(check) === target,
  )
  return (
    matches.find((check) => check.severity === "review") ?? matches[0]
  )
}
